export interface Project {
  name: string;
  logo: string;
  image: string[];
  category: string;
  overview: string;
  process: string;
  development: string;
  conclusion: string;
  demo?: string;
  source_code: string;
  table_of_contents: string[];
  tech: { title: string; icon: string }[];
  features: string[];
  created: string;
  pages: string[];
  slug?: string;
}

const projectData: Project[] = [
  {
    name: "Coursa",
    logo: "/images/projects/coursa-logo.png",
    image: [
      "/images/projects/coursa.png",
      "/images/projects/coursa1.png",
      "/images/projects/coursa2.png",
      "/images/projects/coursa3.png",
      "/images/projects/coursa4.png",
      "/images/projects/coursa5.png",
    ],
    category: "E-commerce",
    overview:
      "Coursa is a cutting-edge eCommerce platform for courses, designed to seamlessly connect educators and learners. It offers robust features for managing courses, users, and other essential functionalities, making it the ultimate platform for online education.",
    process:
      "The development process involved identifying user needs, designing user-friendly interfaces, implementing robust back-end functionalities, and testing for seamless interaction across features.",
    development:
      "Coursa was built with a modern tech stack including Next.js, TypeScript, and Tailwind CSS. We leveraged Radix UI and Shadcn/UI for building interactive components and ensured secure authentication using Next-Auth.",
    conclusion:
      "Coursa delivers a streamlined experience for learners and educators, providing powerful tools to manage and engage with courses effectively. Its modern design and tech stack make it a scalable solution for e-learning platforms.",
    demo: "https://course-mu-amber.vercel.app/",
    source_code: "https://github.com/Supremache/course",
    table_of_contents: [
      "Overview",
      "Process",
      "Development",
      "Conclusion"
    ],
    tech: [
      { title: "React", icon: "/images/React.png" },
      { title: "TypeScript", icon: "/images/TS.png" },
      { title: "TailwindCSS", icon: "/images/Tailwind.png" },
      { title: "ShadCN UI", icon: "/images/ShadCN.png" },
      { title: "Next.js", icon: "/images/NextJS.png" },
    ],
    features: [
      "Interactive Dashboard with analytics and progress tracking",
      "Role-based User Management system",
      "Effortless Course Management tools",
      "Category and content organization",
      "Reviews and ratings for courses",
    ],
    created: "2025-01-21",
    pages: [
      "/admin",
      "/admin/dashboard",
      "/admin/dashboard/overview",
      "/admin/dashboard/courses",
      "/admin/dashboard/courses/new",
      "/admin/dashboard/courses/catagory",
      "/admin/dashboard/members",
      "/courses",
      "/courses/[catagory]",
      "/courses/[catagory]/[course]",
      "/profile",
      "/profile/settings",
      "/profile/messages",
      "/profile/courses",
      "/profile/reviews",
      "/profile/instructors",
      "/login",
      "/register",
    ],
  },
  {
    name: "SELECT 2.0",
    logo: "/images/projects/select.svg",
    image: [
      "/images/projects/select.png",
      "/images/projects/select-1.png",
      "/images/projects/select-features.png",
      "/images/projects/select-integrations.png",
      "/images/projects/select-pricing.png",
      "/images/projects/select-usage-groups.png",
      "/images/projects/select-visibility.png",
    ],
    category: "Landing Page",
    overview: `SELECT 2.0 is a sleek and modern redesign of the SELECT landing page, aimed at delivering a user-friendly and visually stunning interface. This project replicates the provided Figma designs with high fidelity, utilizing advanced front-end technologies to ensure a seamless, responsive, and scalable user experience.`,
    process: `<b>Design Decisions:</b> The SELECT 2.0 project was guided by a commitment to design precision and user experience. Leveraging Agile methodologies, we followed iterative cycles to refine the design and functionality. Each phase of the project—planning, prototyping, development, and testing—focused on achieving a high level of design fidelity and ensuring that the final product met user expectations.<br><br>
    <b>Development Approach:</b> We adopted a modular design strategy using React components for scalability. TailwindCSS was employed for rapid styling, while ShadCN UI provided pre-styled components to speed up development without compromising on aesthetics.<br><br>
    <b>Key Considerations:</b> Accessibility and performance were prioritized, ensuring the landing page catered to a wide audience and offered quick load times on all devices.`,
    development: `<b>Technological Highlights:</b> The SELECT 2.0 landing page employs a robust technology stack, including React Router v7 for efficient navigation and TypeScript for type safety. TailwindCSS ensured that the design was responsive and visually cohesive across all screen sizes.<br><br>
    The development team followed industry best practices, emphasizing clean, maintainable code and reusable components. Real-time tracking and performance optimizations were integrated to align with modern web standards.`,
    conclusion: `The SELECT 2.0 project exemplifies a modern, user-focused approach to web design and development. By combining cutting-edge technologies with a meticulous attention to detail, we have created a platform that not only meets but exceeds user expectations. SELECT 2.0 sets a new standard for landing page design, paving the way for future enhancements.`,
    source_code: "https://github.com/Supremache/front-end/tree/main/app/SELECT%202.0",
    table_of_contents: [
      "Overview",
      "Process",
      "Development",
      "Conclusion"
    ],
    tech: [
      { title: "React", icon: "/images/React.png" },
      { title: "TypeScript", icon: "/images/TS.png" },
      { title: "TailwindCSS", icon: "/images/Tailwind.png" },
      { title: "ShadCN UI", icon: "/images/ShadCN.png" },
    ],
    features: [
      "High Figma design fidelity (98%)",
      "Reusable and scalable components",
      "Optimized performance",
      "Accessible with ARIA attributes",
      "Multi-page layout with dynamic routing",
      "Engaging animations and interactivity",
    ],
    created: "2025",
    pages: [
      "Home",
      "Insights",
      "Integrations",
      "Monitors",
      "Pricing",
      "Savings",
      "Usage Groups",
      "Visibility",
    ],
  },
  {
    name: "Vestiura",
    logo: "/images/projects/vestiura.webp",
    image: [
      "/images/projects/vestiura.png",
      "/images/projects/vestiura-1.png",
      "",
      "/images/projects/vestiura-2.png",
      "/images/projects/vestiura-3.png",
      "/images/projects/vestiura-4.png",
    ],
    category: "E-commerce",
    overview:
      "Vestiura is a modern, visually stunning e-commerce landing page designed for clothing stores. It combines clean, responsive design with advanced animations and dynamic components to elevate the user experience.",
    process:
      "The design and development process for Vestiura followed a user-centric approach, focusing on optimizing visual appeal, responsiveness, and performance for a seamless e-commerce experience. Each component was carefully crafted and iterated to ensure usability and accessibility.",
    development:
      "Built using React and TypeScript, the project leverages Tailwind CSS for utility-first styling, Framer Motion for smooth animations, and ShadCN for reusable and scalable components. The use of type safety and modular architecture ensures maintainability and extensibility.",
    conclusion:
      "Vestiura showcases the capabilities of modern web technologies in creating a high-performance, visually appealing e-commerce solution. Its responsive design and interactive animations provide an engaging user experience for all device sizes.",
    source_code: "https://github.com/Supremache/front-end/tree/main/app/vestiura",
    table_of_contents: [
      "Overview",
      "Process",
      "Development",
      "Conclusion"
    ],
    tech: [
      { title: "React", icon: "/images/React.png" },
      { title: "TypeScript", icon: "/images/TS.png" },
      { title: "TailwindCSS", icon: "/images/Tailwind.png" },
      { title: "ShadCN UI", icon: "/images/ShadCN.png" },
      { title: "Framer Motion", icon: "/images/Frammer.png" },
    ],
    features: [
      "Responsive Design",
      "Interactive Animations",
      "Dynamic Components",
      "Type Safety",
      "Fast Loading",
      "Modern UI",
    ],
    created: "2025",
    pages: [
      "Landing Page",
    ],
  },
  {
    name: "Istidafa",
    logo: "/images/projects/istidafa-logo.svg",
    image: [
      "/images/projects/istidafa.png",
      "/images/projects/istidafa-poster.png",
      "/images/projects/istidafa-review.png",
      "/images/projects/istidafa-blog.png",
      "/images/projects/istidafa-payment.png",
      "/images/projects/istidafa-hosting.png",
    ],
    category: "Web Application",
    overview: "The <b>Modern Web Hosting Platform</b> offers a seamless and intuitive experience for users seeking reliable and flexible web hosting solutions. Designed to simplify the often complex process of choosing and managing web hosting services, this web application empowers users to effortlessly explore and purchase hosting plans that best suit their needs. <br><br> With a user-friendly interface, dynamic features, and a streamlined approach to hosting management, the platform redefines how users interact with web hosting. Whether for individuals, small businesses, or large enterprises, the platform delivers a robust solution to support diverse web hosting requirements, enabling users to make informed decisions with ease and confidence.",
    process: "<b>Design Decisions</b>The development of the <b>Modern Web Hosting Platform</b> was driven by a focus on user experience, aiming to simplify the process of purchasing and managing web hosting services. Using <b>Agile</b> methodologies, the project followed iterative cycles of planning, design, development, testing, and deployment. This approach allowed for flexibility in adapting to user feedback and market changes, ensuring that the platform continuously evolved to meet user needs. <br><br> In terms of design, our team prioritized creating a clean, intuitive, and responsive interface. The goal was to make the complex process of web hosting accessible to all users, from beginners to experienced developers. Through careful user research and design sprints, we ensured that every element of the platform, from navigation to purchase flows, was optimized for ease of use and clarity.<br><br><b>Advantages of the Platform</b><br>The <b>Modern Web Hosting Platform</b> sets itself apart from competitors by offering an optimized, scalable solution with a high degree of user personalization. By integrating <b>React</b> and <b>Material-UI</b>, the platform delivers a dynamic and responsive interface that adapts seamlessly to different devices and screen sizes.<br><br>The platform’s unique combination of streamlined design and powerful backend functionality ensures that users can quickly compare hosting options, access detailed product information, and complete purchases with minimal friction. With a focus on performance and reliability, the platform offers a superior experience, helping businesses of all sizes find the best hosting solutions to power their digital presence.",
    development: "The development of the <b>Modern Web Hosting Platform</b> was executed with a strong emphasis on scalability, performance, and user-friendliness. <b>React</b> was chosen as the core front-end technology for its flexibility and speed, ensuring that the platform could handle increasing user traffic and provide a fast, responsive experience. Additionally, <b>Material-UI</b> was incorporated to enhance the visual design and improve the overall user experience with a consistent, modern interface. <br><br> The development process was characterized by frequent sprints and collaborative feedback loops, which allowed the team to quickly respond to issues and optimize features in real-time. Each iteration focused on improving the platform’s core functionalities, such as search and comparison features, account management, and secure payment processing. As the platform evolves, ongoing testing and refinement ensure that it remains robust, scalable, and aligned with user expectations.",
    conclusion: "In conclusion, the <b>Modern Web Hosting Platform</b> represents a significant advancement in the way users interact with web hosting services. By combining cutting-edge technologies like <b>React</b> and <b>Material-UI</b> with a user-centric design approach, the platform not only simplifies the hosting selection process but also ensures that users enjoy a smooth, responsive experience throughout.<br><br>With the goal of increasing user engagement by <b>25%</b> within six months of launch, the platform’s continued focus on optimization and user feedback is key to its future success. The <b>Modern Web Hosting Platform</b> is set to become a go-to solution for users seeking reliable, flexible, and scalable web hosting services, offering a transformative experience in the digital hosting space.",
    demo: "https://webhosting-w.web.app/",
    source_code: "https://github.com/Supremache/front-end/tree/main/app/Istidafa",
    table_of_contents: [
      "Overview",
      "Process",
      "Development",
      "Conclusion"
    ],
    tech: [
      { title: "React", icon: "/images/React.png" },
      { title: "Material-UI & Joy UI (MUI)", icon: "/images/MUI.png" },
      { title: "JavaScript", icon: "/images/JS.png" },
      { title: "Framer Motion", icon: "/images/Frammer.png" }
    ],
    features: [
      "Fully responsive",
      "Highly customizable",
      "Modern Light/Dark theme design",
      "Multi-Page Structure",
      "Interactive UI Components",
      "Smooth Animations",
      "Detailed instruction & full support"
    ],
    created: "2024",
    pages: [
      "Home",
      "Login",
      "Signup",
      "About",
      "Contact",
      "Domain Registration",
      "Features",
      "Features (CMS)",
      "Services",
      "Services (CMS)",
      "Hosting Pricing",
      "Payment",
      "Blog",
      "Blog (CMS)",
      "FAQ",
      "404"
    ]
  },
  {
    name: "Light Blue",
    logo: "/images/projects/light-blue-logo.svg",
    image: [
      "/images/projects/light-blue.png",
      "/images/projects/light-blue-poster.png",
      "/images/projects/light-blue-tablet-phone.png",
      "",
      "/images/projects/light-blue-phone.png",
      "/images/projects/light-blue-labtop.png",
    ],
    category: "Dashboard",
    overview: "The <b>Light Blue Dashboard</b> is a dynamic and interactive data visualization platform designed to provide users with a seamless and visually engaging experience. Built with <b>React</b> and <b>Material-UI (MUI)</b>, this dashboard empowers users to explore and analyze data through intuitive, interactive charts powered by <b>Nivo Charts</b>. With a focus on user experience, the platform offers a modern, responsive interface that adapts to a variety of devices and screen sizes, ensuring smooth navigation and real-time data insights for individuals, teams, and businesses. <br><br><br> Whether for monitoring key performance indicators (KPIs), tracking business metrics, or analyzing complex datasets, <b>Light Blue</b> transforms data into actionable insights, all within an elegant and easy-to-use interface. Its responsive design, coupled with advanced features such as dark mode and customizable charts, delivers an engaging user experience tailored to diverse needs.",
    process: "<b>Design Decisions</b><br><br><i>The development of <b>Light Blue</b> was driven by a commitment to creating an intuitive and visually appealing dashboard that prioritizes user engagement and data accessibility.</i> Adopting <b>Agile</b> methodologies, the project followed iterative cycles that emphasized continuous feedback and adaptation. Each phase—planning, design, development, testing, and deployment—focused on improving the user interface (UI) and enhancing data visualization capabilities. <br><br><br> The design process centered on creating a clean, modern, and flexible UI using <b>Material-UI (MUI)</b> components, ensuring consistency and ease of use. The integration of <b>Nivo Charts</b> was a critical decision, as it allowed for interactive, customizable visualizations that could cater to a variety of data types. The dashboard’s design also prioritized performance and responsiveness, ensuring that users could interact with the platform fluidly across both mobile and desktop devices. <br><br><br> <b>Advantages of the Dashboard</b><br><br> <i><b>Light Blue</b> stands out by offering a powerful combination of flexibility, performance, and user-centric design.</i> The dashboard’s integration with <b>Nivo Charts</b> enables users to interact with their data in a highly engaging way, adjusting chart types, exploring different visual elements, and gaining deeper insights through real-time data manipulation.",
    development: "<b>The development of <i>Light Blue</i></b> was executed with an emphasis on performance, scalability, and a smooth user experience. <b>React</b> was chosen as the core front-end technology due to its flexibility and ability to efficiently handle complex UI updates and interactions. The use of <b>Material-UI (MUI)</b> ensured that the design was both modern and accessible, with ready-to-use components that allowed the development team to focus on functionality and customization. <br><br> The integration of <b>Nivo Charts</b> allowed the dashboard to deliver visually rich and interactive data visualizations. Each component, from charts to navigation elements, was designed with performance in mind, ensuring quick load times and a responsive experience across devices.",
    conclusion: "<b>In conclusion</b>, the <i><b>Light Blue Dashboard</b></i> represents a significant leap forward in data visualization and user interaction. By combining the power of <b>React</b>, <b>Material-UI</b>, and <b>Nivo Charts</b>, the platform offers a cutting-edge solution for users who need to visualize, analyze, and interact with their data in a modern and intuitive environment. <br><br> With a focus on responsiveness, interactivity, and user engagement, <b>Light Blue</b> is set to become an essential tool for businesses and individuals seeking powerful, real-time data insights. As the platform evolves, ongoing optimizations and user feedback will continue to enhance the experience, making <b>Light Blue</b> a go-to dashboard solution for data-driven decision-making.",
    demo: "",
    source_code: "https://github.com/Supremache/front-end/tree/main/app/modern-responsive-dashboard",
    table_of_contents: [
      "Overview",
      "Process",
      "Development",
      "Conclusion"
    ],
    tech: [
      { title: "React", icon: "/images/React.png" },
      { title: "Material-UI(MUI)", icon: "/images/MUI.png" },
      { title: "JavaScript", icon: "/images/JS.png" },
      { title: "Nivo Charts", icon: "/images/D3.png" }
    ],
    features: [
      "Fully responsive",
      "Highly customizable",
      "Modern Light/Dark theme design",
      "Multi-Page Structure",
      "Interactive UI Components",
    ],
    created: "2023",
    pages: [
      "Home", "Manage team", "Contact", "Calendar", "Bar chart", "Pie chart", "Line chart", "Geography",
      "404"
    ]
  },
  {
    name: "Art Waves",
    logo: "/images/projects/artwaves-logo.svg",
    image: [
      "/images/projects/artwaves.png",
      "/images/projects/artwaves-poster.png",
      "/images/projects/artwaves-1.png",
      "",
      "/images/projects/artwaves-2.png",
      "/images/projects/artwaves-3.png",
    ],
    category: "Landing Page",
    overview: "The <b>Artwaves</b> landing page is an innovative digital art marketplace designed to empower artists by showcasing their unique creations and enabling the buying and selling of modern digital art. Built using <b>React</b> and styled with <b>Tailwind CSS</b>, Artwaves provides a seamless, visually engaging experience for both artists and art enthusiasts. The platform offers a clean, modern interface optimized for showcasing digital art, featuring a gallery that allows users to explore, purchase, and interact with art in an intuitive environment. Whether you are an artist looking to showcase your portfolio or a collector looking for the latest in digital art, Artwaves serves as the perfect platform. <br><br><br> With a responsive design and easy-to-navigate layout, the Artwaves landing page ensures that users enjoy a smooth experience across various devices, allowing for effortless browsing, buying, and selling of digital art.",
    process: "<b>Design Decisions</b><br><br><i>The development of <b>Artwaves</b> was driven by the goal of creating an interactive and visually appealing platform that prioritizes user experience and accessibility to digital art.</i> Adopting <b>Agile</b> methodologies, the project followed iterative cycles focusing on design, development, and user feedback. Each phase, from planning to deployment, focused on refining the user interface (UI) and ensuring a seamless experience for visitors. <br><br><br> The design process focused on creating a modern, clean, and user-friendly interface using <b>Tailwind CSS</b>, ensuring that the platform is both visually appealing and functional. The emphasis on digital art presentation led to the creation of a gallery-style layout that showcases artworks beautifully, allowing users to explore the gallery and discover new pieces easily. Performance and responsiveness were key priorities, ensuring that the website operates smoothly across different devices, including mobile and desktop. <br><br><br> <b>Advantages of the Platform</b><br><br> <i><b>Artwaves</b> offers an engaging and intuitive platform for artists to showcase their work and for collectors to find unique digital art. The use of <b>Tailwind CSS</b> allows for a highly customizable, flexible design, enabling the platform to quickly adapt to new features and changes.</i>",
    development: "<b>The development of <i>Artwaves</i></b> focused on creating a smooth and scalable experience for users. <b>React</b> was chosen for its flexibility and its ability to handle dynamic content updates efficiently, allowing for quick browsing and interaction with the gallery. <b>Tailwind CSS</b> provided a utility-first design system, making it easier for the development team to create a clean, responsive, and custom design without the need for heavy CSS overhead. <br><br> With a strong focus on delivering a seamless experience, the landing page was optimized for fast loading times, smooth navigation, and an engaging gallery view. The platform's design emphasizes performance and ensures that users have a responsive and fluid experience whether browsing on desktop or mobile devices.",
    conclusion: "<b>In conclusion</b>, the <i><b>Artwaves</b></i> landing page provides a powerful and intuitive platform for digital artists to showcase their work and for collectors to discover and purchase modern digital art. By leveraging <b>React</b> and <b>Tailwind CSS</b>, the platform offers a clean and responsive design that ensures a smooth user experience. <br><br> Artwaves is poised to become a go-to destination for both artists and buyers in the world of digital art, offering a seamless way to interact with and enjoy art online. As the platform evolves, user feedback and continuous optimization will ensure that Artwaves remains a cutting-edge digital art marketplace.",
    demo: "",
    source_code: "https://github.com/Supremache/front-end/tree/main/app/art-waves-gallery",
    table_of_contents: [
      "Overview",
      "Process",
      "Development",
      "Conclusion"
    ],
    tech: [
      { title: "React", icon: "/images/React.png" },
      { title: "Tailwind", icon: "/images/Tailwind.png" },
      { title: "JavaScript", icon: "/images/JS.png" },
    ],
    features: [
      "Fully responsive",
      "Highly customizable",
      "Interactive UI Components",
    ],
    created: "2024",
    pages: [
      "Landing Page"
    ]
  }
];

export async function getAllProjects(): Promise<Project[]> {
  return projectData.map((project) => ({
    ...project,
    slug: project.name.replace(/\s+/g, "-").trim().toLowerCase()
  }));
}

export async function findProject({ projectSlug }: { projectSlug: string }): Promise<Project | undefined> {
  const projects = await getAllProjects();
  return projects.find((project) => project.slug === projectSlug);
}
